import { useEffect, useState } from 'react'
import { useNavigate, Link } from '@tanstack/react-router'
import {
  Search,
  Plus,
  Star,
  Clock,
  Check,
  X,
  ChevronRight,
  Calendar,
  Phone,
  MapPin,
  Mail,
  UserPlus,
  Shield,
  UserCheck,
  MoreVertical,
  Trash2,
  Send,
  Pencil,
  Heart,
} from 'lucide-react'
import { useAuthStore } from '../lib/auth'
import { api } from '../lib/api'
import { DashboardLayout } from '../components/DashboardLayout'

interface Person {
  id: string
  email: string
  phone: string | null
  name: string
  avatarUrl: string | null
  role: 'admin' | 'approver' | 'special_guest' | 'guest'
  status: 'pending' | 'active' | 'suspended' | 'invited'
  skipApproval: boolean
  discountPercent: number | null
  canBookOutOfHours: boolean
  voluntaryDonation: boolean
  canCreateRecurringBookings?: boolean
  recurringPaymentType?: 'invoice' | 'donation'
  recurringFixedCost?: number | null
  recurringDonationUrl?: string | null
  totalBookings: number
  confirmedBookings: number
  pendingBookings: number
  lastBooking: string | null
  joinedAt: string | null
  invitedAt: string | null
  createdAt: string
}

interface PersonDetail extends Person {
  phone?: string | null
  address?: string | null
  membershipId: string | null
}

interface PersonBooking {
  id: string
  roomId: string
  roomName: string
  title: string | null
  startTime: string
  endTime: string
  status: string
  totalPrice: string
  createdAt: string
}

interface PendingInvite {
  id: string
  type: string
  role: string | null
  invitedBy: string | null
  expiresAt: string
  usedAt: string | null
  createdAt: string
  isExpired: boolean
}

interface AccessRequest {
  id: string
  roomName: string | null
  preferredDate: string | null
  preferredTime: string | null
  message: string | null
  status: string
  handledBy: string | null
  handledAt: string | null
  responseNote: string | null
  createdAt: string
}

const ROLE_CONFIG: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  admin: {
    label: 'Admin',
    color: 'bg-teal-100 text-teal-700',
    icon: <Shield className="w-3 h-3" />,
  },
  approver: {
    label: 'Approver',
    color: 'bg-blue-100 text-blue-700',
    icon: <UserCheck className="w-3 h-3" />,
  },
  special_guest: {
    label: 'Special Guest',
    color: 'bg-amber-100 text-amber-700',
    icon: <Star className="w-3 h-3" />,
  },
  guest: { label: 'Guest', color: 'bg-gray-100 text-gray-700', icon: null },
}

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  active: { label: 'Active', color: 'bg-green-100 text-green-700' },
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  suspended: { label: 'Suspended', color: 'bg-red-100 text-red-700' },
  invited: { label: 'Invited', color: 'bg-blue-100 text-blue-700' },
}

export function PeoplePage() {
  const navigate = useNavigate()
  const {
    user,
    currentOrg,
    memberships,
    isAuthenticated,
    isLoading: authLoading,
    checkAuth,
  } = useAuthStore()

  const [people, setPeople] = useState<Person[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  // Search and filter
  const [searchQuery, setSearchQuery] = useState('')
  const [filterRole, setFilterRole] = useState<
    'all' | 'admin' | 'approver' | 'special_guest' | 'guest'
  >('all')

  // Person detail modal
  const [selectedPerson, setSelectedPerson] = useState<PersonDetail | null>(null)
  const [personBookings, setPersonBookings] = useState<PersonBooking[]>([])
  const [personPendingInvites, setPersonPendingInvites] = useState<PendingInvite[]>([])
  const [personAccessRequests, setPersonAccessRequests] = useState<AccessRequest[]>([])
  const [isLoadingDetail, setIsLoadingDetail] = useState(false)

  // Edit access request state
  const [editingRequestId, setEditingRequestId] = useState<string | null>(null)
  const [editRequestForm, setEditRequestForm] = useState({
    preferredDate: '',
    preferredTime: '',
    message: '',
  })
  const [editRequestSaving, setEditRequestSaving] = useState(false)

  // Edit role modal
  const [editingPerson, setEditingPerson] = useState<PersonDetail | null>(null)
  const [editRole, setEditRole] = useState<'admin' | 'approver' | 'special_guest' | 'guest'>(
    'guest'
  )
  const [editSkipApproval, setEditSkipApproval] = useState(false)
  const [editDiscountPercent, setEditDiscountPercent] = useState<number | null>(null)
  const [editCanBookOutOfHours, setEditCanBookOutOfHours] = useState(false)
  const [editVoluntaryDonation, setEditVoluntaryDonation] = useState(false)
  const [editRecurringAllowed, setEditRecurringAllowed] = useState(false)
  const [editRecurringPayment, setEditRecurringPayment] = useState<'invoice' | 'donation'>('invoice')
  const [editRecurringCost, setEditRecurringCost] = useState('')
  const [editRecurringDonationUrl, setEditRecurringDonationUrl] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  // Invite modal
  const [showInviteModal, setShowInviteModal] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'approver' | 'special_guest'>(
    'special_guest'
  )
  const [inviteSaving, setInviteSaving] = useState(false)
  const [inviteError, setInviteError] = useState('')

  // Menu state
  const [openMenuId, setOpenMenuId] = useState<string | null>(null)

  useEffect(() => {
    checkAuth()
  }, [])

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate({ to: '/login' })
    }
  }, [authLoading, isAuthenticated, navigate])

  // Redirect non-admins
  useEffect(() => {
    if (!authLoading && isAuthenticated && currentOrg?.role !== 'admin') {
      navigate({ to: '/dashboard' })
    }
  }, [authLoading, isAuthenticated, currentOrg, navigate])

  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      loadPeople()
    }
  }, [authLoading, isAuthenticated])

  // Close menu when clicking outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (!(e.target as HTMLElement).closest('[data-menu]')) {
        setOpenMenuId(null)
      }
    }
    document.addEventListener('click', handleClick)
    return () => document.removeEventListener('click', handleClick)
  }, [])

  const loadPeople = async () => {
    try {
      setIsLoading(true)
      const data = await api.people.list()
      setPeople(data.people)
    } catch (err: any) {
      setError(err.message || 'Failed to load people')
    } finally {
      setIsLoading(false)
    }
  }

  const loadPersonDetail = async (personId: string) => {
    try {
      setIsLoadingDetail(true)
      const data = await api.people.get(personId)
      setSelectedPerson(data.person)
      setPersonBookings(data.bookings)
      setPersonPendingInvites(data.pendingInvites || [])
      setPersonAccessRequests(data.accessRequests || [])
    } catch (err: any) {
      setError(err.message || 'Failed to load person details')
    } finally {
      setIsLoadingDetail(false)
    }
  }

  const openEditModal = (person: PersonDetail) => {
    setEditingPerson(person)
    setEditRole(person.role)
    setEditSkipApproval(person.skipApproval)
    setEditDiscountPercent(person.discountPercent)
    setEditCanBookOutOfHours(person.canBookOutOfHours)
    setEditVoluntaryDonation(person.voluntaryDonation)
    setEditRecurringAllowed(person.canCreateRecurringBookings || false)
    setEditRecurringPayment(person.voluntaryDonation ? 'donation' : person.recurringPaymentType || 'invoice')
    setEditRecurringCost(person.recurringFixedCost == null ? '' : String(person.recurringFixedCost))
    setEditRecurringDonationUrl(person.recurringDonationUrl || '')
    setSaveError('')
  }

  const handleSaveAccessRequest = async () => {
    if (!editingRequestId) return
    setEditRequestSaving(true)
    try {
      const result = await api.bookings.updateAccessRequest(editingRequestId, {
        preferredDate: editRequestForm.preferredDate || undefined,
        preferredTime: editRequestForm.preferredTime || undefined,
        message: editRequestForm.message || undefined,
      })
      if (result.request) {
        setPersonAccessRequests(prev =>
          prev.map(r => r.id === editingRequestId ? { ...r, ...result.request } : r)
        )
      }
      setEditingRequestId(null)
    } catch (err: any) {
      alert(err.message || 'Failed to update request')
    } finally {
      setEditRequestSaving(false)
    }
  }

  const handleSaveRole = async () => {
    if (!editingPerson) return

    setIsSaving(true)
    setSaveError('')

    try {
      await api.people.update(editingPerson.id, {
        role: editRole,
        skipApproval: editSkipApproval,
        discountPercent: editVoluntaryDonation ? 100 : editDiscountPercent,
        canBookOutOfHours: editCanBookOutOfHours,
        voluntaryDonation: editVoluntaryDonation,
        canCreateRecurringBookings: editRecurringAllowed,
        recurringPaymentType: editRecurringPayment,
        recurringFixedCost: editRecurringCost ? Number(editRecurringCost) : null,
        recurringDonationUrl: editRecurringDonationUrl || null,
      })

      setEditingPerson(null)
      loadPeople()

      // Refresh detail view if open
      if (selectedPerson?.id === editingPerson.id) {
        loadPersonDetail(editingPerson.id)
      }
    } catch (err: any) {
      setSaveError(err.message || 'Failed to update person')
    } finally {
      setIsSaving(false)
    }
  }

  const handleInvite = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!inviteEmail || !inviteName) return

    setInviteSaving(true)
    setInviteError('')

    try {
      await api.people.invite({
        email: inviteEmail,
        name: inviteName,
        role: inviteRole,
      })

      setShowInviteModal(false)
      setInviteEmail('')
      setInviteName('')
      setInviteRole('special_guest')
      loadPeople()
    } catch (err: any) {
      setInviteError(err.message || 'Failed to invite person')
    } finally {
      setInviteSaving(false)
    }
  }

  const handleRemove = async (personId: string) => {
    const person = people.find((p) => p.id === personId)
    const confirmMsg =
      person?.status === 'invited'
        ? 'Cancel this invitation?'
        : 'Remove this person from your organisation?'

    if (!confirm(confirmMsg)) return

    try {
      await api.people.remove(personId)
      loadPeople()
      if (selectedPerson?.id === personId) {
        setSelectedPerson(null)
        setPersonBookings([])
      }
    } catch (err: any) {
      alert(err.message || 'Failed to remove person')
    }
    setOpenMenuId(null)
  }

  const handleResendInvite = async (personId: string) => {
    try {
      await api.people.resendInvite(personId)
      alert('Invitation resent')
    } catch (err: any) {
      alert(err.message || 'Failed to resend invitation')
    }
    setOpenMenuId(null)
  }

  // Filter people
  const filteredPeople = people.filter((person) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      if (
        !person.name.toLowerCase().includes(query) &&
        !person.email.toLowerCase().includes(query)
      ) {
        return false
      }
    }

    // Role filter
    if (filterRole !== 'all' && person.role !== filterRole) return false

    return true
  })

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-'
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    })
  }

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!isAuthenticated || !user) {
    return null
  }

  const currentMembership = memberships.find((m) => m.orgSlug === currentOrg?.orgSlug)
  const isAdmin = currentMembership?.role === 'admin'

  if (!isAdmin) {
    return (
      <DashboardLayout title="Access Denied">
        <div className="text-center py-12">
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>
          <p className="text-gray-600">You need admin permissions to view this page.</p>
          <Link
            to="/dashboard"
            className="mt-4 inline-block text-primary-600 hover:text-primary-700"
          >
            Return to Dashboard
          </Link>
        </div>
      </DashboardLayout>
    )
  }

  const headerActions = (
    <button
      onClick={() => setShowInviteModal(true)}
      className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
    >
      <UserPlus className="w-4 h-4" />
      Invite
    </button>
  )

  // Stats
  const adminCount = people.filter((p) => p.role === 'admin').length
  const approverCount = people.filter((p) => p.role === 'approver').length
  const specialGuestCount = people.filter((p) => p.role === 'special_guest').length
  const guestCount = people.filter((p) => p.role === 'guest').length

  return (
    <DashboardLayout
      title="Guests"
      subtitle="Everyone who books or helps run your organisation"
      actions={headerActions}
    >
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            placeholder="Search by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Role Filter Cards */}
      <div className="mb-4 sm:mb-6 grid grid-cols-4 sm:grid-cols-5 gap-2 sm:gap-4">
        <button
          onClick={() => setFilterRole(filterRole === 'admin' ? 'all' : 'admin')}
          className={`text-left rounded-lg border-2 px-2 py-2 sm:p-4 transition-all ${
            filterRole === 'admin'
              ? 'border-teal-500 bg-teal-50 ring-2 ring-teal-200'
              : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-teal-50/50'
          }`}
        >
          <div className="text-lg sm:text-2xl font-bold text-teal-600">{adminCount}</div>
          <div className="text-xs sm:text-sm text-gray-500">Admins</div>
        </button>
        <button
          onClick={() => setFilterRole(filterRole === 'approver' ? 'all' : 'approver')}
          className={`text-left rounded-lg border-2 px-2 py-2 sm:p-4 transition-all ${
            filterRole === 'approver'
              ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200'
              : 'border-gray-200 bg-white hover:border-blue-300 hover:bg-blue-50/50'
          }`}
        >
          <div className="text-lg sm:text-2xl font-bold text-blue-600">{approverCount}</div>
          <div className="text-xs sm:text-sm text-gray-500">Approvers</div>
        </button>
        <button
          onClick={() => setFilterRole(filterRole === 'special_guest' ? 'all' : 'special_guest')}
          className={`text-left rounded-lg border-2 px-2 py-2 sm:p-4 transition-all ${
            filterRole === 'special_guest'
              ? 'border-amber-500 bg-amber-50 ring-2 ring-amber-200'
              : 'border-gray-200 bg-white hover:border-amber-300 hover:bg-amber-50/50'
          }`}
        >
          <div className="text-lg sm:text-2xl font-bold text-amber-600">{specialGuestCount}</div>
          <div className="text-xs sm:text-sm text-gray-500 truncate">Special</div>
        </button>
        <button
          onClick={() => setFilterRole(filterRole === 'guest' ? 'all' : 'guest')}
          className={`text-left rounded-lg border-2 px-2 py-2 sm:p-4 transition-all ${
            filterRole === 'guest'
              ? 'border-gray-500 bg-gray-100 ring-2 ring-gray-300'
              : 'border-gray-200 bg-white hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <div className="text-lg sm:text-2xl font-bold text-gray-600">{guestCount}</div>
          <div className="text-xs sm:text-sm text-gray-500">Guests</div>
        </button>
        {filterRole !== 'all' && (
          <button
            onClick={() => setFilterRole('all')}
            className="text-left rounded-lg border-2 border-dashed border-gray-300 bg-white px-2 py-2 sm:p-4 transition-all hover:border-gray-400 hover:bg-gray-50 flex items-center justify-center col-span-4 sm:col-span-1"
          >
            <div className="text-xs sm:text-sm text-gray-500 font-medium">Clear Filter</div>
          </button>
        )}
      </div>

      {/* People Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Person
                </th>
                <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Role
                </th>
                <th className="hidden sm:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="hidden md:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Bookings
                </th>
                <th className="hidden lg:table-cell px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Last Active
                </th>
                <th className="px-3 sm:px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPeople.length === 0 ? (
                <tr>
                  <td colSpan={6} className="px-3 sm:px-6 py-12 text-center text-gray-500">
                    {searchQuery || filterRole !== 'all'
                      ? 'No people match your search criteria'
                      : 'No people found. Invite someone to get started!'}
                  </td>
                </tr>
              ) : (
                filteredPeople.map((person) => {
                  const roleConfig = ROLE_CONFIG[person.role]
                  const statusConfig = STATUS_CONFIG[person.status]
                  const isCurrentUser = person.id === user?.id

                  return (
                    <tr
                      key={person.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => loadPersonDetail(person.id)}
                    >
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 font-medium text-sm sm:text-base">
                            {person.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="ml-2 sm:ml-4 min-w-0">
                            <div className="text-xs sm:text-sm font-medium text-gray-900 flex items-center gap-1 sm:gap-2">
                              <span className="truncate max-w-[120px] sm:max-w-none">
                                {person.name}
                              </span>
                              {isCurrentUser && (
                                <span className="text-xs text-gray-400">(you)</span>
                              )}
                            </div>
                            <div className="text-xs sm:text-sm text-gray-500 truncate max-w-[140px] sm:max-w-none">
                              {person.email}
                            </div>
                            {person.phone && (
                              <div className="text-xs text-gray-400 truncate max-w-[140px] sm:max-w-none">
                                {person.phone}
                              </div>
                            )}
                            {/* Mobile-only status badge */}
                            <div className="sm:hidden mt-1">
                              <span
                                className={`inline-flex items-center px-1.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}
                              >
                                {statusConfig.label}
                              </span>
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center gap-1 px-1.5 sm:px-2.5 py-0.5 rounded-full text-xs font-medium ${roleConfig.color}`}
                        >
                          {roleConfig.icon}
                          <span className="hidden xs:inline">{roleConfig.label}</span>
                        </span>
                        {person.skipApproval && person.role !== 'admin' && (
                          <span
                            className="ml-1 sm:ml-2 inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700"
                            title="Bookings auto-approved"
                          >
                            <Check className="w-3 h-3" />
                          </span>
                        )}
                      </td>
                      <td className="hidden sm:table-cell px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusConfig.color}`}
                        >
                          {statusConfig.label}
                        </span>
                      </td>
                      <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {person.totalBookings > 0 ? (
                            <span>
                              {person.confirmedBookings} confirmed
                              {person.pendingBookings > 0 && (
                                <span className="text-yellow-600 ml-1">
                                  ({person.pendingBookings} pending)
                                </span>
                              )}
                            </span>
                          ) : (
                            <span className="text-gray-400">No bookings</span>
                          )}
                        </div>
                      </td>
                      <td className="hidden lg:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatDate(person.lastBooking || person.joinedAt)}
                      </td>
                      <td className="px-3 sm:px-6 py-3 sm:py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="relative" data-menu>
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              setOpenMenuId(openMenuId === person.id ? null : person.id)
                            }}
                            className="p-1 text-gray-400 hover:text-gray-600 rounded"
                          >
                            <MoreVertical className="w-4 h-4 sm:w-5 sm:h-5" />
                          </button>

                          {openMenuId === person.id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  loadPersonDetail(person.id)
                                  setOpenMenuId(null)
                                }}
                                className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                              >
                                <ChevronRight className="w-4 h-4" />
                                View Details
                              </button>

                              {person.status === 'invited' && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleResendInvite(person.id)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Send className="w-4 h-4" />
                                  Resend Invitation
                                </button>
                              )}

                              {!isCurrentUser && (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleRemove(person.id)
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                >
                                  <Trash2 className="w-4 h-4" />
                                  {person.status === 'invited' ? 'Cancel Invitation' : 'Remove'}
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Person Detail Modal */}
      {selectedPerson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between shrink-0">
              <h2 className="text-lg font-semibold text-gray-900">Person Details</h2>
              <button
                onClick={() => {
                  setSelectedPerson(null)
                  setPersonBookings([])
                  setPersonPendingInvites([])
                  setPersonAccessRequests([])
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {isLoadingDetail ? (
              <div className="flex-1 flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              </div>
            ) : (
              <div className="flex-1 overflow-y-auto">
                {/* Person Info */}
                <div className="p-6 border-b border-gray-200">
                  <div className="flex items-start gap-4">
                    <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center text-primary-600 text-2xl font-medium">
                      {selectedPerson.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-xl font-semibold text-gray-900">
                          {selectedPerson.name}
                        </h3>
                        <span
                          className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${ROLE_CONFIG[selectedPerson.role].color}`}
                        >
                          {ROLE_CONFIG[selectedPerson.role].icon}
                          {ROLE_CONFIG[selectedPerson.role].label}
                        </span>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_CONFIG[selectedPerson.status].color}`}
                        >
                          {STATUS_CONFIG[selectedPerson.status].label}
                        </span>
                      </div>

                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center gap-2">
                          <Mail className="w-4 h-4" />
                          <a
                            href={`mailto:${selectedPerson.email}`}
                            className="text-blue-600 hover:underline"
                          >
                            {selectedPerson.email}
                          </a>
                        </div>
                        {selectedPerson.phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="w-4 h-4" />
                            <a
                              href={`tel:${selectedPerson.phone}`}
                              className="text-blue-600 hover:underline"
                            >
                              {selectedPerson.phone}
                            </a>
                          </div>
                        )}
                        {selectedPerson.address && (
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            {selectedPerson.address}
                          </div>
                        )}
                      </div>

                      {/* Privileges */}
                      {(selectedPerson.skipApproval ||
                        selectedPerson.discountPercent ||
                        selectedPerson.canBookOutOfHours ||
                        selectedPerson.voluntaryDonation) && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {selectedPerson.skipApproval && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-green-100 text-green-700 text-xs">
                              <Check className="w-3 h-3" /> Auto-approve bookings
                            </span>
                          )}
                          {selectedPerson.voluntaryDonation && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-amber-100 text-amber-700 text-xs">
                              <Heart className="w-3 h-3" /> Voluntary donation
                            </span>
                          )}
                          {selectedPerson.discountPercent && !selectedPerson.voluntaryDonation && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-700 text-xs">
                              {selectedPerson.discountPercent}% discount
                            </span>
                          )}
                          {selectedPerson.canBookOutOfHours && (
                            <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-teal-100 text-teal-700 text-xs">
                              <Clock className="w-3 h-3" /> Out of hours booking
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Action Button */}
                    {selectedPerson.status !== 'invited' && selectedPerson.id !== user?.id && (
                      <div>
                        <button
                          onClick={() => openEditModal(selectedPerson)}
                          className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200"
                        >
                          Edit Role
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="mt-4 grid grid-cols-4 gap-4">
                    <div className="text-center p-3 bg-gray-50 rounded-lg">
                      <div className="text-xl font-bold text-gray-900">{personBookings.length}</div>
                      <div className="text-xs text-gray-500">Total Bookings</div>
                    </div>
                    <div className="text-center p-3 bg-green-50 rounded-lg">
                      <div className="text-xl font-bold text-green-600">
                        {
                          personBookings.filter(
                            (b) => b.status === 'confirmed' || b.status === 'completed'
                          ).length
                        }
                      </div>
                      <div className="text-xs text-gray-500">Confirmed</div>
                    </div>
                    <div className="text-center p-3 bg-yellow-50 rounded-lg">
                      <div className="text-xl font-bold text-yellow-600">
                        {personBookings.filter((b) => b.status === 'pending').length}
                      </div>
                      <div className="text-xs text-gray-500">Pending</div>
                    </div>
                    <div className="text-center p-3 bg-red-50 rounded-lg">
                      <div className="text-xl font-bold text-red-600">
                        {
                          personBookings.filter(
                            (b) => b.status === 'cancelled' || b.status === 'denied'
                          ).length
                        }
                      </div>
                      <div className="text-xs text-gray-500">Cancelled</div>
                    </div>
                  </div>
                </div>

                {/* Pending Invites */}
                {personPendingInvites.length > 0 && (
                  <div className="px-6 pb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Invitations</h4>
                    <div className="space-y-2">
                      {personPendingInvites.map((invite) => (
                        <div
                          key={invite.id}
                          className={`p-3 rounded-lg border ${invite.usedAt ? 'border-green-200 bg-green-50' : invite.isExpired ? 'border-red-200 bg-red-50' : 'border-blue-200 bg-blue-50'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Send className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-900">
                                {invite.role === 'admin' ? 'Admin' : invite.role === 'approver' ? 'Approver' : 'Special Guest'} invite
                              </span>
                              {invite.usedAt ? (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-700">Accepted</span>
                              ) : invite.isExpired ? (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">Expired</span>
                              ) : (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">Pending</span>
                              )}
                            </div>
                          </div>
                          <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                            {invite.invitedBy && <div>Invited by {invite.invitedBy}</div>}
                            <div>Sent {new Date(invite.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                            {!invite.usedAt && (
                              <div>
                                {invite.isExpired ? 'Expired' : 'Expires'}{' '}
                                {new Date(invite.expiresAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </div>
                            )}
                            {invite.usedAt && (
                              <div>Accepted {new Date(invite.usedAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Access Requests */}
                {personAccessRequests.length > 0 && (
                  <div className="px-6 pb-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Access Requests</h4>
                    <div className="space-y-2">
                      {personAccessRequests.map((req) => (
                        <div
                          key={req.id}
                          className={`p-3 rounded-lg border ${req.status === 'approved' ? 'border-green-200 bg-green-50' : req.status === 'denied' ? 'border-red-200 bg-red-50' : 'border-yellow-200 bg-yellow-50'}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium text-gray-900">
                                {req.roomName || 'General access'}
                              </span>
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                req.status === 'approved' ? 'bg-green-100 text-green-700'
                                  : req.status === 'denied' ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}>
                                {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                              </span>
                            </div>
                            {isAdmin && (
                              <button
                                onClick={() => {
                                  if (editingRequestId === req.id) {
                                    setEditingRequestId(null)
                                  } else {
                                    setEditingRequestId(req.id)
                                    setEditRequestForm({
                                      preferredDate: req.preferredDate || '',
                                      preferredTime: req.preferredTime || '',
                                      message: req.message || '',
                                    })
                                  }
                                }}
                                className="p-1 text-gray-400 hover:text-gray-600 hover:bg-white/50 rounded transition-colors"
                                title="Edit request details"
                              >
                                <Pencil className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>

                          {editingRequestId === req.id ? (
                            <div className="mt-2 space-y-2">
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-0.5">Preferred Date</label>
                                <input
                                  type="date"
                                  value={editRequestForm.preferredDate}
                                  onChange={e => setEditRequestForm(f => ({ ...f, preferredDate: e.target.value }))}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-0.5">Preferred Time</label>
                                <input
                                  type="text"
                                  value={editRequestForm.preferredTime}
                                  onChange={e => setEditRequestForm(f => ({ ...f, preferredTime: e.target.value }))}
                                  placeholder="e.g. 14:00 - 18:00"
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                              <div>
                                <label className="block text-xs font-medium text-gray-600 mb-0.5">Message</label>
                                <textarea
                                  value={editRequestForm.message}
                                  onChange={e => setEditRequestForm(f => ({ ...f, message: e.target.value }))}
                                  rows={2}
                                  className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                                />
                              </div>
                              <div className="flex justify-end gap-2 pt-1">
                                <button
                                  onClick={() => setEditingRequestId(null)}
                                  className="px-3 py-1 text-xs text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                                >
                                  Cancel
                                </button>
                                <button
                                  onClick={handleSaveAccessRequest}
                                  disabled={editRequestSaving}
                                  className="px-3 py-1 text-xs text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50"
                                >
                                  {editRequestSaving ? 'Saving...' : 'Save'}
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="mt-1 text-xs text-gray-500 space-y-0.5">
                              {req.preferredDate && (
                                <div>
                                  Requested: {new Date(req.preferredDate + 'T12:00:00').toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}
                                  {req.preferredTime && ` at ${req.preferredTime}`}
                                </div>
                              )}
                              {req.message && <div className="italic">"{req.message}"</div>}
                              <div>Submitted {new Date(req.createdAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                              {req.handledBy && req.handledAt && (
                                <div>
                                  {req.status === 'approved' ? 'Approved' : 'Denied'} by {req.handledBy} on{' '}
                                  {new Date(req.handledAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                </div>
                              )}
                              {req.responseNote && <div>Note: {req.responseNote}</div>}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Booking History */}
                <div className="p-6">
                  <h4 className="text-sm font-medium text-gray-900 mb-4">Booking History</h4>

                  {personBookings.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-8">No bookings found</p>
                  ) : (
                    <div className="space-y-3">
                      {personBookings.map((booking) => (
                        <div
                          key={booking.id}
                          className="flex items-center gap-4 p-3 rounded-lg border border-gray-200 hover:bg-gray-50"
                        >
                          <div className="flex-1">
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-gray-900">{booking.roomName}</span>
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${
                                  booking.status === 'confirmed' || booking.status === 'completed'
                                    ? 'bg-green-100 text-green-700'
                                    : booking.status === 'pending'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : booking.status === 'cancelled'
                                        ? 'bg-gray-100 text-gray-700'
                                        : 'bg-red-100 text-red-700'
                                }`}
                              >
                                {booking.status}
                              </span>
                            </div>
                            <div className="text-sm text-gray-500">
                              {formatDateTime(booking.startTime)} -{' '}
                              {formatDateTime(booking.endTime)}
                            </div>
                          </div>
                          {parseFloat(booking.totalPrice) > 0 && (
                            <div className="text-sm font-medium text-gray-900">
                              {parseFloat(booking.totalPrice).toFixed(2)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Role Modal */}
      {editingPerson && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full max-h-[90dvh] flex flex-col">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Edit Role</h2>
              <button
                onClick={() => setEditingPerson(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-4 overflow-y-auto">
              {saveError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {saveError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Role</label>
                <select
                  value={editRole}
                  onChange={(e) => { setEditRole(e.target.value as typeof editRole); if (e.target.value === 'guest') setEditRecurringAllowed(false) }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="admin">Admin - Full access to settings and users</option>
                  <option value="approver">Approver - Can approve booking requests</option>
                  <option value="special_guest">Special Guest - Enhanced booking privileges</option>
                  <option value="guest">Guest - Basic booking only</option>
                </select>
              </div>

              <div className="border-t border-border pt-4 space-y-4">
                <label className="flex items-center gap-3 font-medium">
                  <input type="checkbox" checked={editRecurringAllowed} onChange={(event) => { setEditRecurringAllowed(event.target.checked); if (event.target.checked && editRole === 'guest') setEditRole('special_guest') }} className="h-5 w-5" />
                  Allow recurring bookings
                </label>
                {editRecurringAllowed && (
                  <>
                    <div>
                      <label htmlFor="recurring-payment" className="block text-sm font-medium mb-2">Payment for each event</label>
                      <select id="recurring-payment" value={editVoluntaryDonation ? 'donation' : editRecurringPayment} disabled={editVoluntaryDonation} onChange={(event) => setEditRecurringPayment(event.target.value as 'invoice' | 'donation')} className="w-full min-h-11 border border-input rounded-md px-3 bg-white">
                        <option value="invoice">Email invoice after use</option>
                        <option value="donation">Voluntary donation after use</option>
                      </select>
                    </div>
                    {editRecurringPayment === 'invoice' && !editVoluntaryDonation ? (
                      <div>
                        <label htmlFor="recurring-price" className="block text-sm font-medium mb-2">Fixed price per event</label>
                        <input id="recurring-price" type="number" min="0.50" max="100000" step="0.01" value={editRecurringCost} onChange={(event) => setEditRecurringCost(event.target.value)} className="w-full min-h-11 border border-input rounded-md px-3" />
                      </div>
                    ) : (
                      <div>
                        <label htmlFor="recurring-donation-link" className="block text-sm font-medium mb-2">Donation link (optional)</label>
                        <input id="recurring-donation-link" type="url" placeholder="Organisation donation link by default" value={editRecurringDonationUrl} onChange={(event) => setEditRecurringDonationUrl(event.target.value)} className="w-full min-h-11 border border-input rounded-md px-3" />
                      </div>
                    )}
                  </>
                )}
              </div>

              {editRole !== 'guest' && (
                <div className="space-y-4 pt-4 border-t border-gray-200">
                  <p className="text-sm text-gray-600">Special privileges:</p>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={editSkipApproval}
                      onChange={(e) => setEditSkipApproval(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Skip booking approval</div>
                      <div className="text-sm text-gray-500">
                        Bookings are automatically confirmed
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={editCanBookOutOfHours}
                      onChange={(e) => setEditCanBookOutOfHours(e.target.checked)}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Book outside normal hours</div>
                      <div className="text-sm text-gray-500">
                        Can book rooms outside scheduled availability
                      </div>
                    </div>
                  </label>

                  <label className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={editVoluntaryDonation}
                      onChange={(e) => {
                        const checked = e.target.checked
                        setEditVoluntaryDonation(checked)
                        if (checked) setEditDiscountPercent(100)
                      }}
                      className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <div>
                      <div className="font-medium text-gray-900">Voluntary donation</div>
                      <div className="text-sm text-gray-500">
                        Bookings are free. The guest is invited to donate by your donation page
                        link or by card, depending on your donation settings.
                      </div>
                    </div>
                  </label>

                  <div>
                    <label className="block font-medium text-gray-900 mb-1">
                      Discount percentage
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max="100"
                        value={editDiscountPercent ?? ''}
                        onChange={(e) =>
                          setEditDiscountPercent(e.target.value ? parseFloat(e.target.value) : null)
                        }
                        disabled={editVoluntaryDonation}
                        placeholder="0"
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:bg-gray-100 disabled:text-gray-500"
                      />
                      <span className="text-gray-500">%</span>
                    </div>
                    {editVoluntaryDonation && (
                      <p className="mt-1 text-xs text-gray-500">
                        Set to 100% automatically for voluntary donation.
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>

            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
              <button
                onClick={() => setEditingPerson(null)}
                className="px-4 py-2 text-gray-700 hover:text-gray-900 text-sm font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveRole}
                disabled={isSaving}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
              >
                {isSaving && (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                )}
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInviteModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900">Invite Someone</h2>
              <button
                onClick={() => setShowInviteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <form onSubmit={handleInvite} className="p-6 space-y-4">
              {inviteError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
                  {inviteError}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={inviteName}
                  onChange={(e) => setInviteName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="John Smith"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={inviteRole}
                  onChange={(e) => setInviteRole(e.target.value as any)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="admin">Admin</option>
                  <option value="approver">Approver</option>
                  <option value="special_guest">Special Guest</option>
                </select>
                <p className="mt-1 text-xs text-gray-500">
                  {inviteRole === 'admin' && 'Full access to settings, users, and all rooms'}
                  {inviteRole === 'approver' && 'Can approve or reject booking requests'}
                  {inviteRole === 'special_guest' && 'Can book rooms with optional privileges'}
                </p>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2 text-gray-700 hover:text-gray-900 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={inviteSaving || !inviteEmail || !inviteName}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 disabled:opacity-50"
                >
                  {inviteSaving && (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  )}
                  Send Invitation
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </DashboardLayout>
  )
}
